import CloseIcon from '@mui/icons-material/Close';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { Box, Button, Paper, Typography } from '@mui/material';
import i18n from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';
import BannersContainer from '../../components/BannersContainer';
import MovingCard from '../../components/MovingCard';
import SortMenu from '../../components/SortMenu';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
    selectIsSearch,
    selectParams,
    selectParamsFilter,
    selectProductListItems,
    selectProductListStatus,
    selectProductListHasMore,
    selectProductListTotalCount,
    selectProductListPageNumber,
    selectCurrentSearch,
    selectSelectedCurrency,
    selectUserId
} from '../../redux/selectors';
import { resetParams } from '../../redux/slices/params';
import { fetchItems, PRODUCT_LIST_PAGE_SIZE, ProductListParams, resetSearch, setCurrentSearch } from '../../redux/slices/productList';
import Categories from './Categories';
import SelectLocation from '../../components/SelectLocation';
import HomeProductGrid from './HomeProductGrid';
import FeedPagination, { parsePageParam, withPage } from './FeedPagination';
import { Spinner } from './Home.styles';

export { ScrollableContainer, ProductGrid, ItemWrapper, Spinner } from './Home.styles';

/** Space kept above the feed when jumping to a new page (the top bar is fixed) */
const FEED_SCROLL_OFFSET = 80;

interface IProps {}
const Home: React.FC<IProps> = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const userId = useAppSelector(selectUserId);
    const data = useAppSelector(selectProductListItems);
    const status = useAppSelector(selectProductListStatus);
    const hasMore = useAppSelector(selectProductListHasMore);
    const totalCount = useAppSelector(selectProductListTotalCount);
    const loadedPage = useAppSelector(selectProductListPageNumber);
    const CurrentSearch = useAppSelector(selectCurrentSearch);
    const isSearched = useAppSelector(selectIsSearch);
    const paramFilter = useAppSelector(selectParamsFilter);
    const paramState = useAppSelector(selectParams);
    const selectedCurrency = useAppSelector(selectSelectedCurrency);
    const showCurrency = selectedCurrency || 'UZS';

    const loading = status === 'loading';
    const [chosenSortValue, setChosenSortValue] = React.useState('');
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    /** The page lives in the URL (`?page=3`) so it survives refresh, sharing and back/forward. */
    const page = parsePageParam(searchParams.get('page'));
    const [sort, setSort] = React.useState<ProductListParams['sortby'] | null>(null);
    const [extraFilter, setExtraFilter] = React.useState<Record<string, unknown>>({});
    const [lang, setLang] = React.useState(i18n.language);
    const [retryToken, setRetryToken] = React.useState(0);
    const [maxKnownPage, setMaxKnownPage] = React.useState(1);
    const feedTopRef = React.useRef<HTMLDivElement>(null);

    const handleSort = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    /** Everything that selects *which* ads are listed. Changing any of it starts again from page 1. */
    const query = React.useMemo(
        () => ({
            ...paramState,
            sortby: (sort ?? paramState?.sortby) as ProductListParams['sortby'],
            filter: { ...(paramState?.filter ?? {}), ...extraFilter } as ProductListParams['filter'],
            search: CurrentSearch,
            lang,
            showCurrency
        }),
        [paramState, sort, extraFilter, CurrentSearch, lang, showCurrency]
    );
    const queryKey = React.useMemo(() => JSON.stringify(query), [query]);
    const queryRef = React.useRef(query);
    queryRef.current = query;

    const lastSortInvocationRef = React.useRef<{ key: string; at: number } | null>(null);

    const sortFunc = React.useCallback(
        (sortParams: { columName: string; type: 'asc' | 'desc' }, filterParams?: Record<string, unknown>) => {
            const key = `${sortParams.columName}-${sortParams.type}-${JSON.stringify(filterParams ?? {})}`;
            const now = Date.now();
            if (lastSortInvocationRef.current?.key === key && now - lastSortInvocationRef.current.at < 500) {
                return;
            }
            lastSortInvocationRef.current = { key, at: now };

            setSort(sortParams);
            setExtraFilter(filterParams ?? {});
            dispatch(setCurrentSearch(''));
        },
        [dispatch]
    );

    const handleClearSearch = () => {
        dispatch(resetParams());
        dispatch(resetSearch());
    };

    React.useEffect(() => {
        if (paramFilter?.parentCategoryId === 0 && paramFilter?.categoryId === 0) {
            setExtraFilter((prev) => (Object.keys(prev).length ? {} : prev));
        }
    }, [paramFilter?.parentCategoryId, paramFilter?.categoryId]);

    React.useEffect(() => {
        const handleLangChange = (lng: string) => setLang(lng);
        i18n.on('languageChanged', handleLangChange);
        return () => {
            i18n.off('languageChanged', handleLangChange);
        };
    }, []);

    // Load the requested page. New filters/sort/search/language/currency go back to page 1 first.
    const lastQueryKeyRef = React.useRef<string | null>(null);
    React.useEffect(() => {
        const queryChanged = lastQueryKeyRef.current !== null && lastQueryKeyRef.current !== queryKey;
        lastQueryKeyRef.current = queryKey;
        if (queryChanged) {
            setMaxKnownPage(1);
            if (page !== 1) {
                // `replace` so Back doesn't return to a page number of the previous result set; this re-runs the effect
                setSearchParams((current) => withPage(current.toString(), 1), { replace: true });
                return;
            }
        }
        dispatch(fetchItems({ ...queryRef.current, userId, pageNumber: page }));
    }, [queryKey, page, userId, retryToken, dispatch, setSearchParams]);

    // Remember how far the result set is known to go, so page numbers don't shrink when going back
    React.useEffect(() => {
        if (status === 'idle' && loadedPage === page) {
            setMaxKnownPage((known) => Math.max(known, hasMore ? page + 1 : page));
        }
    }, [status, loadedPage, page, hasMore]);

    // Bring the top of the feed into view when the page changes (not on first load).
    // Jump, don't animate: while the next page loads the grid shrinks to a few skeleton rows, and when the
    // response is quick the browser's scroll anchoring cancels a smooth scroll and leaves the visitor at the footer.
    const previousPageRef = React.useRef(page);
    React.useEffect(() => {
        if (previousPageRef.current === page) return;
        previousPageRef.current = page;
        const top = feedTopRef.current ? feedTopRef.current.getBoundingClientRect().top + window.scrollY - FEED_SCROLL_OFFSET : 0;
        window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    }, [page]);

    const pageCount = totalCount !== null ? Math.max(1, Math.ceil(totalCount / PRODUCT_LIST_PAGE_SIZE)) : Math.max(maxKnownPage, hasMore ? page + 1 : page);
    const failed = status === 'failed';
    const isEmpty = !loading && !failed && !data?.length;

    return (
        <>
            <Paper sx={{ p: 2, justifyContent: 'center', justifyItems: 'baseline', borderRadius: 6 }} elevation={0}>
                <BannersContainer />
            </Paper>

            <Categories />

            {CurrentSearch.length === 0 && <MovingCard />}

            <Paper elevation={0} ref={feedTopRef}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignContent: 'space-between', px: 2 }}>
                    <SelectLocation />
                    <Box>
                        {!!CurrentSearch && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body1" component="span" sx={{ margin: 2, fontWeight: 400 }}>
                                    {t('web.main-searchedString')}{' '}
                                    <Typography component="span" color="warning.main" fontWeight={600} variant="body1">
                                        &apos;{CurrentSearch}&apos;
                                    </Typography>
                                </Typography>
                                {!isSearched && <CloseIcon onClick={handleClearSearch} />}
                            </Box>
                        )}
                        <Typography variant="subtitle1" align="left" sx={{ fontSize: 18, fontWeight: 600, m: 3 }}>
                            {chosenSortValue}
                        </Typography>
                    </Box>

                    <Button endIcon={<ImportExportIcon />} onClick={handleSort}>
                        <Typography>{t('sort_by.sort')}</Typography>
                    </Button>
                </Box>

                {failed ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 4 }} role="alert">
                        <Typography variant="body1" component="p" fontWeight={600}>
                            {t('web.main-load-error')}
                        </Typography>
                        <Button variant="outlined" onClick={() => setRetryToken((n) => n + 1)}>
                            {t('web.try-again')}
                        </Button>
                    </Box>
                ) : isEmpty ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, flexGrow: 1, py: 3 }}>
                        <Typography variant="body1" component="p" fontWeight={600}>
                            {page > 1 ? t('web.main-page-empty') : t('web.main-noItems')}
                        </Typography>
                        {page > 1 && (
                            <Button component={RouterLink} to={{ pathname: location.pathname, search: withPage(location.search, 1) }} variant="outlined">
                                {t('web.main-page-first')}
                            </Button>
                        )}
                    </Box>
                ) : (
                    <>
                        <HomeProductGrid loading={loading} items={data ?? []} />
                        {!loading && <FeedPagination page={page} pageCount={pageCount} />}
                    </>
                )}
            </Paper>

            <SortMenu open={open} onClose={handleClose} anchorEl={anchorEl} setChosenSortValue={setChosenSortValue} sortFunction={sortFunc} />
        </>
    );
};

export default Home;
